import { isUndefined } from 'lodash'
import moment from 'moment'
import { PaginationType } from '@src/screens/Events/models/events.types'
import { Sort } from '@src/models/app.types'

export interface Variables {
  pagination: PaginationType
  filter: Omit<Sort, 'types'> & {
    types: { key: string; value: string[] }[]
  }
}

export interface FilterPayload {
  startDate: string
  endDate: string
  period?: string
  types: {
    key: string
    value: string[]
  }[]
}

export interface Payload {
  filter: FilterPayload
}

interface DateRangeFilter {
  startDate?: string
  endDate?: string
}

// Current-day data can update after scans without reliable cache invalidation,
// so any range including today should always fetch fresh data.
const shouldUseDateRangeCache = (filter: DateRangeFilter = {}) => {
  const { startDate, endDate } = filter

  if (!startDate || !endDate) {
    return true
  }

  const startDateMoment = moment(startDate, [moment.ISO_8601, 'YYYY-MM-DD'], true)
  const endDateMoment = moment(endDate, [moment.ISO_8601, 'YYYY-MM-DD'], true)

  if (!startDateMoment.isValid() || !endDateMoment.isValid()) {
    return true
  }

  return !moment().isBetween(startDateMoment, endDateMoment, 'day', '[]')
}

const listFilterCacheKey = (name: string, variables: Variables) => {
  const {
    pagination: { type, page, pageSize, endTime } = {},
    filter: { startDate, endDate, orderBy, order, query, startHour, endHour, types = [] } = {},
  } = variables

  const filterTypes = types.flatMap(({ key, value }) => [key, ...value])

  const dateRange = [startDate, endDate]
  const ordering = [orderBy, order]
  const filters = [startHour, endHour, query, ...filterTypes]
  const pagination = type === 'date' ? [endTime, pageSize] : [endTime, page, pageSize]
  const parts = [name, ...dateRange, ...pagination, ...ordering, ...filters]

  return parts.filter((part) => !isUndefined(part) && part !== '').join(':')
}

const dateRangeCacheKey = (name: string, payload: Payload) => {
  const { filter: { startDate, endDate } = {} } = payload
  return `${name}:${startDate}:${endDate}`
}

const dateRangeTypesCacheKey = (name: string, payload: Payload) => {
  const {
    filter: { types = [], period = '' },
  } = payload

  const keys = types.map((type) => type.key)
  const values = types.flatMap((type) => type.value)

  const sortedKeysValues = keys.concat(values).sort()

  const keyValuesPart = sortedKeysValues.join(':')

  const dateRangeKeyPart = dateRangeCacheKey(name, payload)

  return `${period}${dateRangeKeyPart}:${keyValuesPart}`
}

export default {
  listFilterCacheKey,
  dateRangeCacheKey,
  dateRangeTypesCacheKey,
  shouldUseDateRangeCache,
}
